import React, {useEffect, useRef, useState} from "react";
import {Box, IconButton, Stack} from "@mui/material";
import {ArrowLeftSvg, ArrowRightSvg, BulletGreenSvg, BulletWhiteSvg} from "@/pages/landing-page/utils/Svgs";
import {TestimonyCard} from "@/pages/landing-page/utils/TestimonyCard";
import {TestimonyDTO} from "@/pages/landing-page/landing-page-data";
import {isTouchSupported, resolveSwipeDirection, TouchPoint} from "@/pages/landing-page/utils/gallery-swipe";

export interface GalleryCarouselProps {
    items: TestimonyDTO[];
}

interface SwipeProps {
    onTouchStart?: (event: React.TouchEvent) => void;
    onTouchEnd?: (event: React.TouchEvent) => void;
    onTouchCancel?: () => void;
    touchAction?: "pan-y pinch-zoom";
}

/**
 * Touch handlers for the gallery viewport, empty on a device without touch so its markup stays unchanged.
 * A gesture that starts with more than one finger is a zoom and is ignored.
 */
const useGallerySwipe = (prev: () => void, next: () => void): SwipeProps => {
    const [touchSupported] = useState(() => isTouchSupported({ navigator, window }));
    const startRef = useRef<TouchPoint | null>(null);

    if (!touchSupported) {
        return {};
    }
    return {
        onTouchStart: (event) => {
            const touch = event.touches[0];
            startRef.current = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
        },
        onTouchEnd: (event) => {
            const start = startRef.current;
            startRef.current = null;
            const touch = event.changedTouches[0];
            if (!start || !touch) {
                return;
            }
            const direction = resolveSwipeDirection(start, { x: touch.clientX, y: touch.clientY });
            if (direction === "next") {
                next();
            } else if (direction === "prev") {
                prev();
            }
        },
        onTouchCancel: () => {
            startRef.current = null;
        },
        touchAction: "pan-y pinch-zoom"
    };
};

interface GalleryPagerProps {
    items: TestimonyDTO[];
    activeIndex: number;
    prev: () => void;
    next: () => void;
    selectIndex: (index: number) => void;
}

const GalleryPager = ({ items, activeIndex, prev, next, selectIndex }: GalleryPagerProps) => (
    <Stack
        data-cy="gallery-pager"
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 3 }}
    >
        <IconButton onClick={prev}>
            <ArrowLeftSvg />
        </IconButton>

        <Stack direction="row" spacing={1}>
            {items.map((info, index) => (
                <IconButton key={info.name} size="small" onClick={() => selectIndex(index)}>
                    {index === activeIndex ? <BulletGreenSvg /> : <BulletWhiteSvg />}
                </IconButton>
            ))}
        </Stack>

        <IconButton onClick={next}>
            <ArrowRightSvg />
        </IconButton>
    </Stack>
);

const useContainerWidth = () => {
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    return { containerRef, containerWidth };
};

const STRIP_GAP_PX = 20;

const stripLayout = (containerWidth: number, activeIndex: number) => {
    const maxItemWidth = 1000;
    const minWidthForFullWidth = 1000;
    const availableWidth = containerWidth - 40; // Account for padding

    const useFullWidth = containerWidth <= minWidthForFullWidth;
    const itemWidth = useFullWidth
        ? availableWidth // Use full available width
        : Math.min(maxItemWidth, availableWidth * 0.8); // 80% of available width, max 1000px

    const offset = (containerWidth - itemWidth) / 2 - activeIndex * (itemWidth + STRIP_GAP_PX);
    return { itemWidth, offset };
};

const GalleryCarousel = ({ items }: GalleryCarouselProps) => {
    const [activeIndex, setActiveIndex] = useState(1);
    const totalItems = items.length;
    const { containerRef, containerWidth } = useContainerWidth();

    const prev = () => setActiveIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
    const next = () => setActiveIndex((prevIndex) => (prevIndex + 1) % totalItems);
    const { touchAction, ...swipeHandlers } = useGallerySwipe(prev, next);
    const { itemWidth, offset } = stripLayout(containerWidth, activeIndex);

    return (
        <Box ref={containerRef} sx={{ width: "100%", overflow: "hidden", py: 4 }}>
            <Box
                {...swipeHandlers}
                sx={{ position: "relative", width: "100%", overflow: "hidden", ...(touchAction && { touchAction }) }}
            >
                <Box
                    data-cy="moving-strip"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: `${STRIP_GAP_PX}px`,
                        transform: `translateX(${offset}px)`,
                        transition: "transform 0.6s ease-in-out",
                        willChange: "transform"
                    }}
                >
                    {items.map((info, index) => (
                        <TestimonyCard
                            key={info.name}
                            image={info.image}
                            text={info.text}
                            name={info.name}
                            active={index === activeIndex}
                            width={itemWidth}
                        />
                    ))}
                </Box>
            </Box>

            <GalleryPager
                items={items}
                activeIndex={activeIndex}
                prev={prev}
                next={next}
                selectIndex={setActiveIndex}
            />
        </Box>
    );
};

export default GalleryCarousel;
